// BM-H398 - s4_guardians. Shown only while a child is under 18 at
// submission (C-08). `allowance.permitted` drives C-09.
//
// BM-H3362 - once there is more than one primary guardian, a substitute
// must say which of them it replaces; once more than one substitute
// replaces the same primary, each needs a priority. C-08 clause 2 (sealed under
// BM-H3858) blocks the Will until this data resolves.
//
// BM-H3363 - cap_gbp alone cannot say whether the figure is an aggregate,
// an annual sum, or per child or per guardian. C-09 blocks without a
// basis, so cap_basis is asked alongside cap_gbp.

import { MoneyInput, NumberInput, QuestionRow, Select, SectionIntro, YesNo, sectionProps } from "./fields";
import { PeopleList } from "./PersonPicker";
import { findPerson, minorChildren } from "./willsPayload";

const ALLOWANCE_CAP_BASIS = [
  { value: "aggregate", label: "A total limit, however it's spent" },
  { value: "annual", label: "A limit per year" },
  { value: "per_child", label: "A limit for each child" },
  { value: "per_guardian", label: "A limit for each guardian" },
];

export default function S4Guardians({ section, setSection, payload, update, person, answers }) {
  const { s, set, nk, setNk } = sectionProps(section, setSection);
  const minors = minorChildren(payload, answers || payload.submission.answers);
  const allowance = s.allowance || {};
  const primary = s.primary || [];
  const substitutes = s.substitutes || [];

  function editSubstitute(i, patch) {
    set({ substitutes: substitutes.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  }

  // BM-H3858: with ONE primary guardian the "which guardian" question is not asked, because it can
  // only be that guardian, so a substitute with no link replaces that one. Without this the priority
  // question was never asked for a single primary and two substitutes could never be put in order.
  const soleGuardian = primary.length === 1 ? primary[0].person_id || null : null;
  const guardianReplaced = (x) => x.replaces_primary_person_id || soleGuardian;

  function renderSubstituteExtra(item, i) {
    const replaced = guardianReplaced(item);
    const samePrimaryCount = replaced
      ? substitutes.filter((x) => guardianReplaced(x) === replaced).length
      : 0;
    return (
      <div style={{ marginTop: 6 }}>
        {primary.length > 1 && (
          <QuestionRow qid="s4_guardians.substitutes.replaces_primary_person_id" label="Which guardian does this substitute replace?" testid={`s4-substitute-${i}-q-replaces`}>
            <Select
              testid={`s4-substitute-${i}-replaces`}
              value={item.replaces_primary_person_id ?? null}
              options={primary.filter((p) => p.person_id).map((p) => ({ value: p.person_id, label: findPerson(payload, p.person_id)?.full_legal_name || "(unnamed)" }))}
              onChange={(v) => editSubstitute(i, { replaces_primary_person_id: v || null })}
            />
          </QuestionRow>
        )}
        {samePrimaryCount > 1 && (
          <QuestionRow qid="s4_guardians.substitutes.priority" label="If more than one substitute could act for the same guardian, what order should they be tried in?" testid={`s4-substitute-${i}-q-priority`}>
            <NumberInput testid={`s4-substitute-${i}-priority`} min="1" value={item.priority} onChange={(v) => editSubstitute(i, { priority: v })} />
          </QuestionRow>
        )}
      </div>
    );
  }

  return (
    <div data-testid="section-s4_guardians">
      <SectionIntro title="Guardians for your children">
        {minors.length > 0
          ? `Because ${minors.map((m) => m.full_legal_name || "a child").join(" and ")} ${minors.length === 1 ? "is" : "are"} under 18, we ask who should look after them if no parent with parental responsibility for them survives you.`
          : "This section applies if you have children under 18."}
      </SectionIntro>

      <PeopleList payload={payload} update={update} items={primary} onChange={(items) => set({ primary: items })} qid="s4_guardians.primary" label="Guardian" testid="s4-primary" exclude={[person?.id, ...minors.map((m) => m.id)]} notKnown={nk("primary")} onNotKnown={(on) => setNk("primary", on)} />

      <div style={{ marginTop: 18 }}>
        <PeopleList payload={payload} update={update} items={substitutes} onChange={(items) => set({ substitutes: items })} qid="s4_guardians.substitutes" label="Substitute guardian" testid="s4-substitutes" exclude={[person?.id, ...minors.map((m) => m.id), ...primary.map((x) => x.person_id).filter(Boolean)]} renderExtra={renderSubstituteExtra} />
      </div>

      <div style={{ marginTop: 18 }}>
        <QuestionRow qid="s4_guardians.allowance.permitted" label="Should the guardian receive a sum of money towards the cost of raising your children?" notKnown={nk("allowance")} onNotKnown={(on) => setNk("allowance", on)}>
          <YesNo testid="s4-allowance-permitted" value={allowance.permitted ?? null} disabled={nk("allowance")} onChange={(v) => set({ allowance: { ...allowance, permitted: v } })} />
        </QuestionRow>
        {allowance.permitted === true && (
          <>
            <QuestionRow qid="s4_guardians.allowance.cap_gbp" label="Roughly how much?">
              <MoneyInput testid="s4-allowance-cap" value={allowance.cap_gbp} onChange={(v) => set({ allowance: { ...allowance, cap_gbp: v } })} />
            </QuestionRow>
            <QuestionRow qid="s4_guardians.allowance.cap_basis" label="Is that a total, a yearly amount, or a figure for each child or each guardian?">
              <Select testid="s4-allowance-basis" value={allowance.cap_basis ?? null} options={ALLOWANCE_CAP_BASIS} onChange={(v) => set({ allowance: { ...allowance, cap_basis: v || null } })} />
            </QuestionRow>
          </>
        )}
      </div>
    </div>
  );
}
